// 3rd Party
import _ from 'lodash';

// Other
import { BRAND_COLORS } from 'brand-guidelines/colors';

import { combineAllReviews } from '../../helpers/common-helpers';

import { LOCATION_FALLBACK, MARKER_ICONS, PREFERRED_LOCATION_TYPES } from './GoogleMapsConstants.ts';

// Exports
export const getMapBoundsFromMarkers = (markers) => {
  let north;
  let east;
  let south;
  let west;

  markers.forEach((marker) => {
    if (north === undefined || marker.position.lat > north) {
      north = marker.position.lat;
    }
    if (east === undefined || marker.position.lng > east) {
      east = marker.position.lng;
    }
    if (south === undefined || marker.position.lat < south) {
      south = marker.position.lat;
    }
    if (west === undefined || marker.position.lng < west) {
      west = marker.position.lng;
    }
  });

  return { north, east, south, west };
};

export const wideMapBounds = (markers) => {
  const marker = markers[0];

  // Based on the zoom amount needed
  const latOffset = 0.001;
  const lngOffset = 0.03;

  const north = marker.position.lat + latOffset;
  const east = marker.position.lng + lngOffset;
  const south = marker.position.lat - latOffset;
  const west = marker.position.lng - lngOffset;

  return { north, east, south, west };
};

export const getMapBounds = (markers, fallbackPosition) => {
  const mapBoundsHandlers = {
    0: () => wideMapBounds([{ position: fallbackPosition }]),
    1: () => wideMapBounds(markers),
  };

  return (
    mapBoundsHandlers[markers.length]?.() ?? getMapBoundsFromMarkers(markers)
  );
};

const isValidBounds = (bounds) => {
  return bounds.north && bounds.east && bounds.south && bounds.west;
};

export const fitBoundsWithMaxZoom = (map, bounds, maxZoom = 14) => {
  if (!isValidBounds(bounds)) {
    return;
  }

  map.fitBounds(bounds);

  const listener = map.addListener('bounds_changed', () => {
    const currentZoom = map.getZoom();
    if (currentZoom && currentZoom > maxZoom) {
      map.setZoom(maxZoom);
    }
    window.google.maps.event.removeListener(listener);
  });
};

export const getMarkerIcons = (result) => {
  let icon = MARKER_ICONS.blankBlue;
  let iconHighlighted = MARKER_ICONS.blankWhiteBlue;

  if (result.isInstantBook) {
    icon = MARKER_ICONS.blankCoralInstant;
    iconHighlighted = MARKER_ICONS.blankWhiteCoralInstant;
  }

  return { icon, iconHighlighted };
};

const getMapMarker = ({
  mapItem,
  icon,
  highlightedIcon,
  index,
  isUserLocation = false,
}) => {
  const reviews = combineAllReviews(mapItem);
  const primaryPhoto = _.get(mapItem, 'providers[0].doctor.primaryPhoto') || {};

  return {
    id: mapItem.id ?? 'user-location',
    position: {
      lat: mapItem.latitude,
      lng: mapItem.longitude,
    },
    icon,
    highlighted: {
      icon: highlightedIcon ?? icon,
      label: {
        color:
          !isUserLocation && mapItem.isInstantBook
            ? BRAND_COLORS.red
            : BRAND_COLORS.blue,
        text: `${index + 1}`,
      },
    },
    isUserLocation,
    ...!isUserLocation && {
      info: {
        image: {
          name: primaryPhoto.name,
          extension: primaryPhoto.extension,
        },
        reviewsCount: reviews.length,
        reviews,
        text: mapItem.name,
      },
      label: {
        color: BRAND_COLORS.white,
        text: `${index + 1}`,
      },
    },
    ...isUserLocation && {
      info: {
        text: 'Your Location',
      },
      address: mapItem.formattedAddress,
    },
  };
};

export const getResultsMapMarkers = (results) =>
  results.map((result, index) => {
    const icons = getMarkerIcons(result);
    return getMapMarker({
      mapItem: result,
      icon: icons.icon,
      highlightedIcon: icons.iconHighlighted,
      index,
    });
  });

export const getClinicMapMarker = (clinic) => {
  const icon = MARKER_ICONS.coralDotted;
  return [
    getMapMarker({ mapItem: clinic, icon, highlightedIcon: icon, index: 0 }),
  ];
};

export const getUserLocationMapMarker = (userLocation) => {
  const icon = MARKER_ICONS.locationPin;
  return [getMapMarker({ mapItem: userLocation, icon, isUserLocation: true })];
};

export const getSingleMarkerBounds = (marker) => {
  const pos = marker.position;
  const latOffset = 0.001;
  const lngOffset = 0.03;

  return {
    north: pos.lat + latOffset,
    east: pos.lng + lngOffset,
    south: pos.lat - latOffset,
    west: pos.lng - lngOffset,
  };
};

export const getResultBounds = (mapBounds, marker) => {
  const lat = marker?.position?.lat || 0;
  const lng = marker?.position?.lng || 0;
  const latOffset = (mapBounds.north - mapBounds.south) / 2;
  const lngOffset = (mapBounds.east - mapBounds.west) / 2;

  return {
    north: lat + latOffset,
    east: lng + lngOffset,
    south: lat - latOffset,
    west: lng - lngOffset,
  };
};

/**
 * Calculate the distance between two geographic coordinates using the Haversine formula
 * This formula accounts for the spherical nature of the Earth
 * Inspired by: https://stackoverflow.com/questions/1502590/calculate-distance-between-two-points-in-google-maps-v3
 *
 * @param {number} lat1 - Latitude of first point in decimal degrees
 * @param {number} lng1 - Longitude of first point in decimal degrees
 * @param {number} lat2 - Latitude of second point in decimal degrees
 * @param {number} lng2 - Longitude of second point in decimal degrees
 * @returns {number} Distance between the two points in kilometers
 */
export const calculateDistanceBetweenCoordinates = (lat1, lng1, lat2, lng2) => {
  const EARTH_RADIUS_KM = 6371;

  // Convert latitude and longitude differences from degrees to radians
  // This is necessary because trigonometric functions work with radians
  const latitudeDifferenceInRadians = (lat2 - lat1) * Math.PI / 180;
  const longitudeDifferenceInRadians = (lng2 - lng1) * Math.PI / 180;

  // Haversine formula calculation: https://en.wikipedia.org/wiki/Haversine_formula
  // Step 1: Calculate the square of half the chord length between the points
  const halfChordSquared =
    Math.pow(Math.sin(latitudeDifferenceInRadians / 2), 2)
    + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180)
    * Math.pow(Math.sin(longitudeDifferenceInRadians / 2), 2);

  // Step 2: Calculate the angular distance in radians
  const angularDistanceInRadians = 2 * Math.atan2(Math.sqrt(halfChordSquared), Math.sqrt(1 - halfChordSquared));

  // Step 3: Calculate the distance by multiplying angular distance by Earth's radius
  return EARTH_RADIUS_KM * angularDistanceInRadians;
};

export const shouldShowSearchHereButton = (
  originalCenter,
  currentCenter,
) => {
  const thresholdKm = 2;

  const distance = calculateDistanceBetweenCoordinates(
    originalCenter.lat,
    originalCenter.lng,
    currentCenter.lat,
    currentCenter.lng,
  );

  return distance > thresholdKm;
};

export const extractLocationName = (results) => {
  for (const result of results) {
    const component = result.address_components?.find((c) =>
      PREFERRED_LOCATION_TYPES.some((type) => c.types.includes(type)));

    if (component) {
      return component.short_name || component.long_name;
    }
  }

  return LOCATION_FALLBACK;
};

export const getLocationName = async (map) => {
  try {
    const center = map.getCenter();
    if (!center) {
      return LOCATION_FALLBACK;
    }

    const geocoder = new window.google.maps.Geocoder();
    const response = await geocoder.geocode({
      location: { lat: center.lat(), lng: center.lng() },
    });

    if (response.results?.length > 0) {
      return extractLocationName(response.results);
    }

    return LOCATION_FALLBACK;
  } catch {
    return LOCATION_FALLBACK;
  }
};

const ALTERNATE_LOCALITY_FIELDS = [
  'sublocality_level_5',
  'sublocality_level_4',
  'sublocality_level_3',
  'sublocality_level_2',
  'sublocality_level_1',
  'sublocality',
  'neighborhood',
  'administrative_area_level_3',
];

const getComponent = (geocodeResult, type, key = 'short_name') => {
  const addressComponents = geocodeResult?.address_components;
  const addressComponent = addressComponents?.find((component) => component.types?.includes(type));

  return addressComponent?.[key];
};

const convertGeocodeResultToAddress = (geocodeResult) => {
  const formattedAddress = geocodeResult.formatted_address;
  const googleCoordinates = geocodeResult.geometry?.location;

  if (!formattedAddress || !googleCoordinates) {
    return null;
  }

  const result = {
    countryCode: getComponent(geocodeResult, 'country'),
    country: getComponent(geocodeResult, 'country', 'long_name'),

    // Region code
    administrative_area_level_1_short: getComponent(geocodeResult, 'administrative_area_level_1'),

    // Region name
    administrative_area_level_1: getComponent(geocodeResult, 'administrative_area_level_1', 'long_name'),

    locality: getComponent(geocodeResult, 'locality') || ALTERNATE_LOCALITY_FIELDS.map((field) => getComponent(geocodeResult, field)).find(Boolean),

    // County
    administrative_area_level_2: getComponent(geocodeResult, 'administrative_area_level_2'),

    // Street name
    street: getComponent(geocodeResult, 'route'),
    number: getComponent(geocodeResult, 'street_number'),

    postalCode: getComponent(geocodeResult, 'postal_code'),

    formatted_address: formattedAddress,
  };

  const isEmptyResult = Object.entries(result)
    .every(([, value]) => !value);

  if (isEmptyResult) {
    return null;
  }

  return result;
};

export const geolocateAddressFromCoordinates = async (coordinates) => {
  if (!window.google?.maps?.Geocoder) {
    throw new Error('Tried to geocode coordinates but Google Maps API is not loaded');
  }

  const geocoder = new window.google.maps.Geocoder();
  const response = await geocoder.geocode({
    location: { lat: coordinates.lat, lng: coordinates.lng },
  });

  const firstResult = response.results?.[0];
  if (!firstResult) {
    throw new Error('Failed to geocode coordinates');
  }

  const address = convertGeocodeResultToAddress(response.results[0]);
  if (!address) {
    throw new Error('Could not determine address from coordinates');
  }

  return address;
};

export const prepareLocationDataForSearch = (coordinates, address) => {
  return {
    latitude: coordinates.lat,
    longitude: coordinates.lng,
    formattedAddress: address.formatted_address,
    address,
  };
};
